import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FitImage from "@ucs/Components/Common/FitImage";
import PageBreadcrumbs, { THEMES } from "@ucs/Overrides/Components/PageBreadcrumbs";
import UCSColors from "@ucs/Utils/UCSColors";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    bgImage = "bgImage",
    title = "title",
    focalPoint = "focalPoint",
    hideContent = "hideContent",
    minHeight = "minHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.bgImage]: string;
        [fields.hideContent]: boolean;
        [fields.focalPoint]:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight";
        [fields.minHeight]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const HeroBannerWrapper = styled("div") <{ minHeight?: string }>`
    display: flex;
    position: relative;
    min-height: ${p => p.minHeight === "" || p.minHeight === undefined || p.minHeight === null ? '30': p.minHeight}vw;
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                height: auto;
            `,
            css`
            `,
            css`
            `,
            css`
            `,
            css`
            `,
        ])};
`;

const HeroBannerTextSection = styled("div")`
    background-color: ${UCSColors.gray01};
    color: white;
    width: 50%;
    height: auto;
    font-size: 20px;
    padding: 32px 2.25em;
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: 1;
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                padding: 1.25rem 30px;
                width: 100%;
                position: static;
            `,
            css`
                width: 100%;
                padding: 1.25rem 30px;
                position: static;
            `,
            css`
                width: 100%;
                padding: 1.25rem 30px;
                position: static;
            `,
        ])};
    > h1 {
        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 40px;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 40px;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
            css`
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                `,
        ])};
    }

    > p {
        margin-bottom: 0px;
        font-size: 20px;
        line-height: 24px;

        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                    font-size: 18px;
                `,
            css`
                    font-size: 18px;
                `,
        ])};
    }
`;

const ProjectHeroBanner: React.FC<Props> = props => {
    const title: string = props.fields.title || props.page.fields.title;
    const bgImage: string = props.fields.bgImage;
    const imageWrapperStyles = {
        css: css`
            position: relative;
            overflow: hidden;
            width: 100%;
            z-index: 0;
        `,
    };

    const imageContainerStyles = {
        css: css`
            flex: 1;
            width: 100%;
            height: 100%;
            position: relative;
            overflow: hidden;
        `,
    };

    return (
        <HeroBannerWrapper minHeight={props.fields.minHeight} id="megaMenuContainer">
            <StyledWrapper {...imageWrapperStyles}>
                <FitImage image={bgImage} focalPoint={props.fields.focalPoint} containerStyles={imageContainerStyles}/>
            </StyledWrapper>
            {!props.fields.hideContent && (
                <HeroBannerTextSection>
                    <PageBreadcrumbs theme={THEMES.LIGHT}></PageBreadcrumbs>
                    <Typography variant="h1" as="h1">
                        {title}
                    </Typography>
                </HeroBannerTextSection>
            )}
        </HeroBannerWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    displayName: "Content Hero Banner",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            defaultValue: "",
            editorTemplate: "TextField",
            fieldType: "General",
        },
        {
            name: fields.bgImage,
            displayName: "Background Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            fieldType: "General",
            name: fields.focalPoint,
            displayName: "Focal Point",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Top Left", value: "topLeft" },
                { displayName: "Top Center", value: "topCenter" },
                { displayName: "Top Right", value: "topRight" },
                { displayName: "Center Left", value: "centerLeft" },
                { displayName: "Center", value: "center" },
                { displayName: "Center Right", value: "centerRight" },
                { displayName: "Bottom Left", value: "bottomLeft" },
                { displayName: "Bottom Center", value: "bottomCenter" },
                { displayName: "Bottom Right", value: "bottomRight" },
            ],
            defaultValue: "center",
            hideEmptyOption: true,
        },
        {
            name: fields.minHeight,
            displayName: "Minimum Height",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            fieldType: "General",
            tooltip: "Calculated in vw units (default value: 30vw)",
        },
        {
            name: fields.hideContent,
            displayName: "Hide content area",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProjectHeroBanner),
    definition,
};

export default widgetModule;
